import { Classes, Switch } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { Search } from "modules/common/components/form/Search";
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { CollapseExpandAll } from "modules/common/components/table/CollapseExpandAll";
import { NoData } from "modules/common/components/table/NoData";
import { Zoomer } from "modules/common/components/zoomer/Zoomer";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { DefaultSelectedOption, renderSingleOption, SimpleSelect } from "modules/common/services/form/select";
import { menuItems } from "modules/root/components/menu/MainMenu";
import { PlanDayHeader } from "modules/spending/workload-plan/components/PlanDayHeader";
import { PeriodRangeSlider } from "modules/spending/workload-plan/components/PeriodRangeSlider";
import { columnLeft, columnsWidth, PlanScale, slotRange } from "modules/spending/workload-plan/models/plan-scale";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useProjectPlanGrid } from "../hooks/useProjectPlanGrid";
import { useVisibleProjects } from "../hooks/useVisibleProjects";
import { buildProjectPlan, ProjectPlanResponse } from "../models/project-plan-builder";
import { useMst } from "modules/root/models/hooks";
import moment from "moment";
import { Milestone, MILESTONE_DAY_FORMAT, MilestoneDraft } from "../models/milestones-store";
import { buildPlanLinks } from "../models/plan-links";
import { applyDraft, PlanDraftPush, PlanOperationInput } from "../models/plan-draft";
import { computeDraft } from "../models/plan-draft-local";
import { absenceBands, computeConflicts, ConflictTarget, openConflicts, PlanConflict } from "../models/plan-conflicts";
import { BusyBand, busyBands } from "../models/plan-busy";
import {
    endPlaces,
    linkCaption,
    LinkRole,
    placeOf as linkPlaceOf,
    PlanLinkEndInput,
    PlanLinkEndResponse,
    PlanLinkResponse,
    PlanLinkType,
} from "../models/plan-dependencies";
import { LinksDialog, LinksTarget } from "./LinksDialog";
import { LinkDragStart, LinksLayer } from "./LinksLayer";
import { BlockHighlight, PROPOSED_PREFIX } from "./BlockHighlight";
import { ApprovalsPanel } from "./ApprovalsPanel";
import { applyApprovals, PlanApproval, PlanApprovalPush } from "../models/plan-approvals";
import { ConflictJumpContext } from "./PlanTrack";
import { MONTHS_LIMITS, ProjectPlanSort, ProjectPlanStoreType } from "../models/project-plan-store";
import { ProjectPlanDetail, ProjectPlanProject } from "../models/project-plan-types";
import { MilestoneEditor } from "./MilestoneEditor";
import { ActiveMilestones, MilestoneAnchor, MilestoneHandlers, TrackViewport } from "./MilestonesLayer";
import { MilestonePlate } from "./MilestonePlate";
import { PlanDraftPanel } from "./PlanDraftPanel";
import { ProjectPlanHelpDialog } from "./ProjectPlanHelpDialog";
import { ConflictsPanel } from "./ConflictsPanel";
import { ShiftFromDialog, ShiftFromTarget } from "./ShiftFromDialog";
import { PROJECT_TRACK_OFFSET, ProjectSection } from "./ProjectSection";
import { planRowKey } from "./ProjectPlanRowView";
import styles from "./ProjectPlanPage.module.scss";
import { useStableNavigate } from "modules/common/components/routing/useStableNavigate";

const menu = menuItems();

/** Сколько ждать раскрытия проекта перед прокруткой к строке, мс: чуть дольше анимации Collapse */
const REVEAL_DELAY = 260;

/** Масштаб сетки */
const SCALE_OPTIONS: SelectItem[] = [
    { id: "day", label: "По дням" },
    { id: "week", label: "По неделям" },
];

/** Порядок проектов */
const SORT_OPTIONS: SelectItem[] = [
    { id: "number", label: "По номеру" },
    { id: "gip", label: "По ГИПу" },
];

/**
 * Кнопка инструкции — как в Табеле План: цвета у типа lightblueish,
 * инлайном только размеры, она мельче обычной.
 */
const HELP_BUTTON_STYLE: React.CSSProperties = {
    height: "22px",
    minHeight: "22px",
    padding: "0 10px",
    fontSize: "11px",
};

/** Во что раскрывается проект */
const DETAIL_OPTIONS: SelectItem[] = [
    { id: "employees", label: "Сотрудники" },
    { id: "sections", label: "Разделы" },
];

/** Глубина показа: свой набор вариантов для каждого масштаба */
const MONTHS_OPTIONS: { [key in PlanScale]: SelectItem[] } = { day: [], week: [] };

(Object.keys(MONTHS_OPTIONS) as PlanScale[]).forEach((scale) => {
    const limits = MONTHS_LIMITS[scale];

    for (let months = limits.min; months <= limits.max; months++) {
        MONTHS_OPTIONS[scale].push({ id: String(months), label: `${months} мес.` });
    }
});

interface ProjectPlanPageProps {
    store: ProjectPlanStoreType;
    /** Права пользователя: по ним решается, какие подписи ведут в карточки */
    access: string[];
}

/**
 * «Плановый график работ»: проекты слева, недели месяцев справа.
 * Страница только показывает — редактирование плана живёт в Табеле Плане.
 */
export const ProjectPlanPage = observer((props: ProjectPlanPageProps) => {
    const { store, access } = props;

    const links = useMemo(() => buildPlanLinks(access), [access]);
    const { milestones } = useMst();

    // ответ сервера храним как есть: черновик накладывается поверх него при каждой правке,
    // без повторного запроса графика
    const [response, setResponse] = useState<ProjectPlanResponse | null>(null);
    const [loaded, setLoaded] = useState(false);

    // правки графика копятся на странице (как в Табеле План) — предпросмотр считается здесь же,
    // без запроса на каждое перетаскивание
    const operations = store.operations;
    const localDraft = useMemo(() => computeDraft(response, operations), [response, operations]);

    const drafted = useMemo(() => (response ? applyDraft(response, localDraft.view) : null), [response, localDraft]);

    /* ---------- согласование чужой части сдвига (этап 6.5) ---------- */

    /** Открытые запросы на согласование, которые касаются пользователя */
    const [approvals, setApprovals] = useState<PlanApproval[]>([]);
    const [approvalsOpen, setApprovalsOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    const loadApprovals = useCallback(async () => {
        const list: PlanApproval[] | null = await store.fetchApprovals();

        if (list) {
            setApprovals(list);
        }

        return list;
    }, [store]);

    // предложенное место объектов из запросов — фиолетовым контуром поверх плана с правками;
    // контур — ghost, поэтому конфликты и связи считаются по drafted без него
    const proposed = useMemo(() => (drafted ? applyApprovals(drafted, approvals) : null), [drafted, approvals]);

    const plan = useMemo(() => buildProjectPlan(proposed, store.monthsOnPage), [proposed, store.monthsOnPage]);

    // конфликты считаются по плану с правками: видно и то, что есть, и то, что правка породит
    const conflicts = useMemo(() => computeConflicts(drafted), [drafted]);
    const unresolved = useMemo(() => openConflicts(conflicts), [conflicts]);

    const conflictsByProject = useMemo(() => {
        const result = new Map<string, PlanConflict[]>();

        conflicts.forEach((conflict) =>
            conflict.orderIds.forEach((orderId) => result.set(orderId, [...(result.get(orderId) || []), conflict]))
        );

        return result;
    }, [conflicts]);
    const [selectedSlot, setSelectedSlot] = useState<number | null>(null);

    const gridRef = useRef<HTMLDivElement>(null);

    const load = useCallback(
        async (year?: number, month?: number) => {
            const response: ProjectPlanResponse | null = await store.fetch(year, month);

            if (response) {
                const next = buildProjectPlan(response, store.monthsOnPage);
                setResponse(response);

                // Кнопки «свернуть/развернуть всё» перебирают уже известные ключи
                // (SectionCollapser.setAll), поэтому проект должен попасть в карту
                // до первого клика по нему — иначе кнопки не делают ничего.
                // Уже открытые проекты не трогаем: смена периода не должна их схлопывать.
                next.projects.forEach((project) => {
                    if (!store.collapser.opened.has(project.id)) {
                        store.collapser.set(project.id, false);
                    }
                });
            }

            setLoaded(true);
        },
        [store]
    );

    useEffect(() => {
        document.title = menu.schedules.nested.projectPlan.label;
    }, []);

    useEffect(() => {
        load();
        // черновик живёт между сессиями: открыли страницу — продолжаем правку
        store.loadDraft();
        loadApprovals();
        // грузим один раз на монтирование; период и глубина перезагружают сами
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---------- передвижка сроков: правки копятся, «Сохранить» — в черновик ---------- */

    /** Есть что править: все проекты, свои проекты как ГИП или свои сотрудники */
    const canEdit =
        plan.editAll || plan.editableEmployers.length > 0 || plan.projects.some((project) => project.canEditMilestones);

    const editableEmployers = useMemo(() => new Set(plan.editableEmployers), [plan.editableEmployers]);

    const days = useMemo(() => plan.columns.map((column) => column.day), [plan.columns]);

    /** Колонка по дате — для подсветки конфликтов */
    const columnOf = useMemo(() => new Map(days.map((day, index) => [day, index])), [days]);

    /** Занятость сотрудников по всем проектам — фон строки; строка берёт из неё чужое */
    const busy = useMemo(
        () => (store.showBusy ? busyBands(drafted, days) : undefined),
        [store.showBusy, drafted, days]
    );

    /** Отсутствия сотрудников полосами — видно, где отпуск кончается и куда можно сдвинуть работу */
    // показываются вместе с занятостью на других проектах — тумблер «Другие проекты»
    const absences = useMemo(
        () => (store.showBusy ? absenceBands((drafted && drafted.absences) || [], columnOf) : undefined),
        [store.showBusy, drafted, columnOf]
    );

    /**
     * Правка на странице: сначала считаем её тут же — если по правилам она не проходит
     * (прошлое, нет дней), в список не берём и говорим почему
     */
    const onOperation = useCallback(
        (operation: PlanOperationInput) => {
            const next = [...store.operations, operation];
            const reason = computeDraft(response, next).rejected.get(next.length - 1);

            if (reason) {
                store.notify.warning(reason);
                return;
            }

            store.pushOperation(operation);
        },
        [store, response]
    );

    /** «Сдвинуть с даты»: окно по правому клику на полосе проекта или раздела */
    const [shiftFrom, setShiftFrom] = useState<ShiftFromTarget | null>(null);

    const submitShiftFrom = useCallback(
        (fromDay: string, shift: number) => {
            if (!shiftFrom) {
                return;
            }

            onOperation({
                kind: shiftFrom.kind,
                orderId: shiftFrom.orderId,
                contentGuid: shiftFrom.kind === "section" ? shiftFrom.contentGuid || null : null,
                shift,
                fromDay,
            });
        },
        [onOperation, shiftFrom]
    );

    const applyDraftChanges = useCallback(
        async (force: boolean) => {
            const result = await store.applyDraft(force);

            // план поменялся — график перечитывается (сервер сбросил свой кэш)
            if (result && result.applied) {
                load();

                // каскад упёрся в чужие проекты — туда ушли запросы ГИПам
                if (result.sentForApproval && result.sentForApproval.length) {
                    store.notify.success(
                        `Сдвиг по связям отправлен на согласование ГИПам: ${result.sentForApproval.join(", ")}`
                    );
                    loadApprovals();
                }
            }

            return result;
        },
        [store, load, loadApprovals]
    );

    const gotoMonth = useCallback(
        (year: number, month: number) => {
            if (store.dirty && !window.confirm("Правки графика не сохранены. Перейти к другому периоду?")) {
                return;
            }

            store.setPeriod(year, month);
            setSelectedSlot(null);
            load(year, month);
        },
        [store, load]
    );

    const changeScale = useCallback(
        (scale: PlanScale) => {
            store.setScale(scale);
            setSelectedSlot(null);

            // данных на новый масштаб хватает — сетку пересоберёт useProjectPlanGrid
            if (store.needsReload) {
                load();
            }
        },
        [store, load]
    );

    const changeMonthsOnPage = useCallback(
        (months: number) => {
            store.setMonthsOnPage(months);
            setSelectedSlot(null);

            if (store.needsReload) {
                load();
            }
        },
        [store, load]
    );

    const { grid, periods, monthBreaks, columnsCount } = useProjectPlanGrid(plan, store.scale, store.monthsOnPage);

    const projects = useVisibleProjects(plan.projects, store.pureQuery, columnsCount, store.sort, store.detail);

    const onSelectSlot = useCallback(
        (slot: number) => setSelectedSlot((current) => (current === slot ? null : slot)),
        []
    );

    /** Выделенный столбец в координатах колонок: в недельном масштабе это вся неделя */
    const selectedRange = useMemo(() => {
        if (selectedSlot === null) {
            return null;
        }

        const slot = grid.slots[selectedSlot];

        return slot && !slot.isEmpty ? slotRange(grid, slot.start) : null;
    }, [selectedSlot, grid]);

    const monthsOptions = MONTHS_OPTIONS[store.scale];

    const monthsOption = useMemo(
        () => monthsOptions.find((item) => item.id === String(store.monthsOnPage)) || monthsOptions[0],
        [monthsOptions, store.monthsOnPage]
    );

    const scaleOption = useMemo(
        () => SCALE_OPTIONS.find((item) => item.id === store.scale) || SCALE_OPTIONS[0],
        [store.scale]
    );

    const sortOption = useMemo(
        () => SORT_OPTIONS.find((item) => item.id === store.sort) || SORT_OPTIONS[0],
        [store.sort]
    );

    const detailOption = useMemo(
        () => DETAIL_OPTIONS.find((item) => item.id === store.detail) || DETAIL_OPTIONS[0],
        [store.detail]
    );

    /* ---------- вехи ---------- */

    /** Веха под курсором: тёмная плашка-подсказка, гаснет вместе с курсором */
    const [hoveredMilestone, setHoveredMilestone] = useState<ActiveMilestones | null>(null);

    /** Веха в фокусе (одиночный клик): подсветка и подсказка держатся до клика мимо или Esc */
    const [focusedMilestone, setFocusedMilestone] = useState<ActiveMilestones | null>(null);

    /** Что сейчас выделено: фокус важнее наведения */
    const activeMilestone = focusedMilestone || hoveredMilestone;

    const setActiveMilestone = useCallback((value: ActiveMilestones | null) => {
        setHoveredMilestone(value);
        setFocusedMilestone(value);
    }, []);

    // фокус снимается по Esc и по клику мимо вех
    useEffect(() => {
        if (!focusedMilestone) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && setFocusedMilestone(null);
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Element | null;

            if (!target || !target.closest("[data-milestone]")) {
                setFocusedMilestone(null);
            }
        };

        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("pointerdown", onPointerDown, true);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("pointerdown", onPointerDown, true);
        };
    }, [focusedMilestone]);

    /**
     * Белое окно правки вехи — открывается кликом по вехе, «+» или двойным кликом по дню.
     * column — столбец даты: он подсвечивается, пока окно открыто.
     */
    const [milestoneEditor, setMilestoneEditor] = useState<{
        key: string;
        anchor: MilestoneAnchor;
        items: MilestoneDraft[];
        column: number;
        project: string;
        readOnly: boolean;
    } | null>(null);

    const closeMilestoneEditor = useCallback(() => setMilestoneEditor(null), []);

    /**
     * Видимая часть шкалы в координатах трека: по ней считаются вехи, ушедшие за край
     * экрана. Левые PROJECT_TRACK_OFFSET пикселей экрана закрыты липкой колонкой названий.
     */
    const scrollRef = useRef<HTMLDivElement>(null);
    const [viewport, setViewport] = useState<TrackViewport | null>(null);
    const viewportFrame = useRef<number>(0);

    const measureViewport = useCallback(() => {
        window.cancelAnimationFrame(viewportFrame.current);

        viewportFrame.current = window.requestAnimationFrame(() => {
            const element = scrollRef.current;

            if (!element) {
                return;
            }

            const zoom = store.transform || 1;
            const start = Math.round(element.scrollLeft / zoom);
            const end = Math.round((element.scrollLeft + element.clientWidth) / zoom - PROJECT_TRACK_OFFSET);

            setViewport((current) =>
                current && current.start === start && current.end === end ? current : { start, end }
            );
        });
    }, [store]);

    useEffect(() => {
        measureViewport();
        window.addEventListener("resize", measureViewport);

        return () => {
            window.removeEventListener("resize", measureViewport);
            window.cancelAnimationFrame(viewportFrame.current);
        };
    }, [measureViewport, store.transform, grid, projects.length]);

    // окно правки и подсказка стоят в экранных координатах вехи — при прокрутке отстали бы
    const onGridScroll = useCallback(() => {
        setActiveMilestone(null);
        setMilestoneEditor(null);
        measureViewport();
    }, [measureViewport, setActiveMilestone]);

    /** Прокрутить так, чтобы точка шкалы встала посередине видимой части */
    const revealOnTrack = useCallback(
        (center: number) => {
            const element = scrollRef.current;

            if (!element) {
                return;
            }

            const zoom = store.transform || 1;
            const visibleTrack = element.clientWidth / zoom - PROJECT_TRACK_OFFSET;

            element.scrollTo({ left: Math.max(0, (center - visibleTrack / 2) * zoom), behavior: "smooth" });
        },
        [store]
    );

    /* ---------- конфликты ---------- */

    const navigate = useStableNavigate();
    const [conflictsOpen, setConflictsOpen] = useState(false);
    const [selectedConflict, setSelectedConflict] = useState<string | null>(null);

    /** Строка, к которой перешли кликом по занятости на другом проекте; снимается по Esc */
    const [focusedRow, setFocusedRow] = useState<string | null>(null);

    /**
     * Строки выбранного конфликта: сотрудник в каждом его проекте, а в режиме «Разделы» —
     * в каждом разделе, где лежат дни конфликта. Подсвечены, пока конфликт выбран
     */
    const conflictRows = useCallback(
        (conflict: PlanConflict) => {
            const keys = conflict.orderIds.map((orderId) => planRowKey(orderId, conflict.employerId));

            if (store.detail === "sections") {
                const cells = conflict.cells.length
                    ? conflict.cells
                    : conflict.orderIds.map((orderId) => ({ orderId, contentGuid: conflict.contentGuid }));

                return cells.map((cell) => planRowKey(cell.orderId, conflict.employerId, cell.contentGuid));
            }

            return keys;
        },
        [store.detail]
    );

    const highlightedRows = useMemo(() => {
        const conflict = selectedConflict ? conflicts.find((item) => item.id === selectedConflict) : undefined;
        const keys = conflict ? conflictRows(conflict) : [];

        if (focusedRow) {
            keys.push(focusedRow);
        }

        return keys.length ? new Set(keys) : undefined;
    }, [selectedConflict, conflicts, conflictRows, focusedRow]);

    /** Пометить может тот, кто мог бы конфликт исправить: все проекты, ГИП или начальник отдела */
    const canMarkConflict = useCallback(
        (conflict: PlanConflict) =>
            plan.editAll ||
            editableEmployers.has(conflict.employerId) ||
            conflict.orderIds.some((orderId) => {
                const project = plan.projects.find((item) => item.id === orderId);
                return !!project && project.canEditMilestones;
            }),
        [plan.editAll, plan.projects, editableEmployers]
    );

    /**
     * Прокрутить к строке сотрудника (нет её — к проекту): строка — на трети высоты экрана,
     * день day — на трети ширины; без дня горизонтальная прокрутка не меняется
     */
    const revealRow = useCallback(
        (rowKey: string, orderId: string, day?: string) => {
            // раскрытие рисуется в следующем кадре — мерить строку раньше нельзя
            window.requestAnimationFrame(() => {
                const container = scrollRef.current;
                const block = container
                    ? ((container.querySelector(`[data-plan-row="${rowKey}"]`) ||
                          container.querySelector(`[data-plan-project="${orderId}"]`)) as HTMLElement | null)
                    : null;

                if (!container || !block) {
                    return;
                }

                const zoom = store.transform || 1;
                const column = day ? columnOf.get(day) : undefined;
                const visibleTrack = container.clientWidth / zoom - PROJECT_TRACK_OFFSET;
                const left =
                    column !== undefined
                        ? Math.max(0, (columnLeft(grid, column) - visibleTrack / 3) * zoom)
                        : container.scrollLeft;
                // под липкой шапкой дней
                const top =
                    block.getBoundingClientRect().top -
                    container.getBoundingClientRect().top +
                    container.scrollTop -
                    container.clientHeight / 3;

                container.scrollTo({ left, top: Math.max(0, top), behavior: "smooth" });
            });
        },
        [store, columnOf, grid]
    );

    useEffect(() => {
        if (!focusedRow) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && setFocusedRow(null);
        document.addEventListener("keydown", onKeyDown);

        return () => document.removeEventListener("keydown", onKeyDown);
    }, [focusedRow]);

    /**
     * Клик по занятости сотрудника на другом проекте (или в другом разделе) — раскрыть тот
     * проект и раздел, прокрутить к строке сотрудника там и подсветить её
     */
    const goToBusy = useCallback(
        (band: BusyBand, employerId: string) => {
            const sections = store.detail === "sections";
            const rowKey = planRowKey(band.orderId, employerId, sections ? band.contentGuid : undefined);

            store.collapser.set(band.orderId, true);

            if (sections) {
                store.collapser.set(`${band.orderId}/${band.contentGuid || "none"}`, true);
            }

            setSelectedConflict(null);
            setFocusedRow(rowKey);
            revealRow(rowKey, band.orderId);
        },
        [store, revealRow]
    );

    /* ---------- связи ---------- */

    const planLinks = useMemo(() => (drafted && drafted.links) || [], [drafted]);

    /** Нарушенные связи — красные стрелки */
    const violatedLinks = useMemo(
        () => new Set(conflicts.filter((item) => item.link).map((item) => item.link!.id)),
        [conflicts]
    );

    const [selectedLink, setSelectedLink] = useState<string | null>(null);

    /** Где стоят концы связей по плану с черновиком — для стрелок к свёрнутым строкам */
    const linkPlaces = useMemo(() => endPlaces(drafted), [drafted]);

    const placeOfEnd = useCallback(
        (end: PlanLinkEndResponse, role?: LinkRole) => linkPlaceOf(end, linkPlaces, role),
        [linkPlaces]
    );

    /** X даты на дорожке: левый край первого рабочего дня не раньше даты или правый край последнего не позже */
    const columnX = useCallback(
        (day: string, side: "start" | "end") => {
            if (!days.length || day < days[0] || day > days[days.length - 1]) {
                return null;
            }

            const index =
                side === "start"
                    ? days.findIndex((item) => item >= day)
                    : days.length - 1 - [...days].reverse().findIndex((item) => item <= day);

            if (index < 0 || index >= columnsCount) {
                return null;
            }

            return side === "start" ? columnLeft(grid, index) : columnLeft(grid, index) + columnsWidth(grid, index, 1);
        },
        [days, grid, columnsCount]
    );

    /** Проекты на экране (после поиска): второй конец вне их — якорь, а не стрелка */
    const visibleOrders = useMemo(() => new Set(projects.map((project) => project.id)), [projects]);
    const [linksTarget, setLinksTarget] = useState<LinksTarget | null>(null);

    /** Связь сохранилась — кладём её в ответ графика, без перечитывания всего графика */
    const upsertLink = useCallback((link: PlanLinkResponse) => {
        setResponse((current) =>
            current
                ? {
                      ...current,
                      links: [...(current.links || []).filter((item) => item.id !== link.id), link],
                  }
                : current
        );
    }, []);

    const saveLink = useCallback(
        async (link: {
            id?: string | null;
            type: PlanLinkType;
            lag: number;
            from?: PlanLinkEndInput;
            to?: PlanLinkEndInput;
        }) => {
            const saved: PlanLinkResponse | null = await store.saveLink(link);

            if (saved) {
                upsertLink(saved);
            }

            return saved;
        },
        [store, upsertLink]
    );

    const removeLink = useCallback(
        async (id: string) => {
            const removed = await store.removeLink(id);

            if (removed) {
                setResponse((current) =>
                    current ? { ...current, links: (current.links || []).filter((item) => item.id !== id) } : current
                );
                setSelectedLink(null);
            }

            return removed;
        },
        [store]
    );

    const searchLinkTargets = useCallback(
        (query: string, orderId: string | null, kind: "work" | "section") =>
            store.fetchLinkTargets(query, orderId, kind),
        [store]
    );

    /** Правый клик по полосе работы — её связи */
    const openWorkLinks = useCallback(
        (orderId: string, assignmentId: string, employerId: string) => {
            const project = plan.projects.find((item) => item.id === orderId);
            const row = project ? project.rows.find((item) => item.id === employerId) : undefined;
            const section = project
                ? project.sections.find((item) =>
                      item.rows.some((sectionRow) =>
                          sectionRow.segments.some((segment) => segment.assignmentId === assignmentId)
                      )
                  )
                : undefined;

            setLinksTarget({
                kind: "work",
                orderId,
                assignmentId,
                label: [
                    project ? project.fullInventoryNumber : "",
                    section && section.id ? section.code : "",
                    row ? row.label : "",
                ]
                    .filter(Boolean)
                    .join(" · "),
                canEdit: plan.editAll || editableEmployers.has(employerId) || (!!project && project.canEditMilestones),
            });
        },
        [plan.projects, plan.editAll, editableEmployers]
    );

    const openSectionLinks = useCallback(
        (orderId: string, contentGuid: string, title: string) => {
            const project = plan.projects.find((item) => item.id === orderId);

            setLinksTarget({
                kind: "section",
                orderId,
                contentGuid,
                label: [project ? project.fullInventoryNumber : "", title].filter(Boolean).join(" · "),
                canEdit: plan.editAll || (!!project && project.canEditMilestones),
            });
        },
        [plan.projects, plan.editAll]
    );

    /* ---------- связь протягиванием от точки у конца полосы ---------- */

    const [linkDrag, setLinkDrag] = useState<{ start: LinkDragStart; x: number; y: number } | null>(null);

    const startLink = useCallback((start: LinkDragStart) => setLinkDrag({ start, x: start.x, y: start.y }), []);

    useEffect(() => {
        if (!linkDrag) {
            return;
        }

        const start = linkDrag.start;

        const onMove = (event: PointerEvent) => setLinkDrag({ start, x: event.clientX, y: event.clientY });

        const onUp = (event: PointerEvent) => {
            setLinkDrag(null);

            // полоса под курсором: у работы — work:…, у раздела — section:…
            const target = document
                .elementsFromPoint(event.clientX, event.clientY)
                .map((element) => element.closest("[data-link-end]"))
                .find((element) => !!element);

            const key = target ? target.getAttribute("data-link-end") || "" : "";

            if (!key || key === start.key) {
                return;
            }

            if (key.split(":")[0] !== start.key.split(":")[0]) {
                store.notify.warning("Связывать можно работу с работой или раздел с разделом");
                return;
            }

            // к какому концу цели отпустили: левая половина — начало, правая — конец
            const rect = target!.getBoundingClientRect();
            const side = event.clientX < rect.left + rect.width / 2 ? "start" : "end";
            const type = ((start.side === "end" ? "f" : "s") + (side === "start" ? "s" : "f")) as PlanLinkType;

            saveLink({ type, lag: 0, from: parseEnd(start.key), to: parseEnd(key) });
        };

        const onKey = (event: KeyboardEvent) => event.key === "Escape" && setLinkDrag(null);

        // пока тянешь, браузер выделял бы текст фамилий под курсором
        const onSelect = (event: Event) => event.preventDefault();
        const userSelect = document.body.style.userSelect;
        document.body.style.userSelect = "none";
        window.getSelection()?.removeAllRanges();

        document.addEventListener("pointermove", onMove);
        document.addEventListener("pointerup", onUp, { once: true });
        document.addEventListener("keydown", onKey);
        document.addEventListener("selectstart", onSelect);

        return () => {
            document.removeEventListener("pointermove", onMove);
            document.removeEventListener("pointerup", onUp);
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("selectstart", onSelect);
            document.body.style.userSelect = userSelect;
        };
        // слушатели заводятся на начало протягивания, а не на каждое движение
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkDrag && linkDrag.start, saveLink, store]);

    /** Раскрыть проект (и раздел) конца связи и прокрутить к нему */
    const revealEnd = useCallback(
        (end: PlanLinkEndResponse) => {
            store.collapser.set(end.orderId, true);

            const sections = store.detail === "sections";

            if (sections && end.contentGuid) {
                store.collapser.set(`${end.orderId}/${end.contentGuid}`, true);
            }

            const rowKey = end.employerId
                ? planRowKey(end.orderId, end.employerId, sections ? end.contentGuid || "" : undefined)
                : "";

            // строка связанного объекта подсвечена, как при переходе из панели конфликтов
            setSelectedConflict(null);
            setFocusedRow(rowKey || null);

            // проект раскрывается с анимацией (Collapse) — мерить строку до её конца рано:
            // прокрутка ушла бы туда, где строка была до раскрытия
            window.setTimeout(() => revealRow(rowKey, end.orderId, end.start || undefined), REVEAL_DELAY);
        },
        [store, revealRow]
    );

    /* ---------- каскад: переход к сдвинутому по связям объекту ---------- */

    /** Полосы, подсвеченные рамкой после перехода из списка «Сдвинуто по связям»; снимается Esc или кликом */
    const [highlightKeys, setHighlightKeys] = useState<string[] | null>(null);

    useEffect(() => {
        if (!highlightKeys) {
            return;
        }

        const clear = () => setHighlightKeys(null);
        const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && clear();

        // на перехвате: фокус остаётся на пункте всплывающего списка, а Blueprint гасит Esc у себя
        document.addEventListener("keydown", onKeyDown, true);
        // клик по сетке — пользователь занялся другим; сам клик в списке сюда не доходит
        const grid = gridRef.current;
        grid && grid.addEventListener("pointerdown", clear);

        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
            grid && grid.removeEventListener("pointerdown", clear);
        };
    }, [highlightKeys]);

    /**
     * Клик по объекту в списке каскада: раскрыть его проект (и раздел), прокрутить к строке
     * и новому положению, обвести полосу. Проект свёрнут или строки нет — обводится полоса проекта
     */
    const revealPush = useCallback(
        (push: PlanDraftPush) => {
            const sections = store.detail === "sections";

            store.collapser.set(push.orderId, true);

            // работа без раздела лежит в строке «Без раздела»
            if (sections) {
                store.collapser.set(`${push.orderId}/${push.contentGuid || "none"}`, true);
            }

            const work = push.assignmentId
                ? localDraft.view.works.find((item) => item.id === push.assignmentId)
                : undefined;
            const term =
                push.kind === "section"
                    ? localDraft.view.terms.find(
                          (item) => item.orderId === push.orderId && item.contentGuid === push.contentGuid
                      )
                    : undefined;
            const day =
                work && work.after.length ? work.after[0].from : term ? term.afterStart || undefined : undefined;

            const rowKey =
                push.kind === "work" && push.employerId
                    ? planRowKey(push.orderId, push.employerId, sections ? push.contentGuid || "" : undefined)
                    : "";

            setSelectedConflict(null);
            setFocusedRow(null);
            setHighlightKeys(
                push.kind === "work"
                    ? [`work:${push.assignmentId}`, `project:${push.orderId}`]
                    : [`section:${push.orderId}/${push.contentGuid}`, `project:${push.orderId}`]
            );

            window.setTimeout(() => revealRow(rowKey, push.orderId, day), REVEAL_DELAY);
        },
        [store, localDraft, revealRow]
    );

    /**
     * Двойной клик по дням конфликта: перейти ко второй стороне — другой работе сотрудника
     * (перегрузка) или предшественнику (нарушенная связь) — и обвести её полосу
     */
    const jumpToConflictTarget = useCallback(
        (target: ConflictTarget) => {
            const sections = store.detail === "sections";

            store.collapser.set(target.orderId, true);

            if (sections && (target.assignmentId || target.contentGuid)) {
                store.collapser.set(`${target.orderId}/${target.contentGuid || "none"}`, true);
            }

            const rowKey = target.employerId
                ? planRowKey(target.orderId, target.employerId, sections ? target.contentGuid : undefined)
                : "";

            setSelectedConflict(null);
            setFocusedRow(null);
            setHighlightKeys(
                target.assignmentId
                    ? [`work:${target.assignmentId}`, `project:${target.orderId}`]
                    : target.contentGuid
                      ? [`section:${target.orderId}/${target.contentGuid}`, `project:${target.orderId}`]
                      : [`project:${target.orderId}`]
            );

            window.setTimeout(() => revealRow(rowKey, target.orderId, target.day || undefined), REVEAL_DELAY);
        },
        [store, revealRow]
    );

    /** «Показать» в запросе на согласование: объект и его предложенное место (контур) */
    const revealApprovalPush = useCallback(
        (approval: PlanApproval, push: PlanApprovalPush) => {
            const work = push.assignmentId ? approval.works.find((item) => item.id === push.assignmentId) : undefined;

            jumpToConflictTarget({
                orderId: push.orderId,
                employerId: push.employerId,
                contentGuid: push.contentGuid || "",
                assignmentId: push.assignmentId,
                day: work && work.after.length ? work.after[0].from : null,
                label: push.label,
            });

            // обводим контур предложенного места; нет его на экране — сам объект
            if (push.assignmentId) {
                setHighlightKeys([
                    `${PROPOSED_PREFIX}work:${push.assignmentId}`,
                    `work:${push.assignmentId}`,
                    `project:${push.orderId}`,
                ]);
            }
        },
        [jumpToConflictTarget]
    );

    /** «Причина» в запросе: предшественник по связи — он уже стоит на новом месте */
    const revealApprovalCause = useCallback(
        (push: PlanApprovalPush) => {
            const cause = push.cause;

            if (!cause) {
                return;
            }

            jumpToConflictTarget({
                orderId: cause.orderId,
                employerId: cause.employerId,
                contentGuid: cause.contentGuid || "",
                assignmentId: cause.kind === "work" ? cause.assignmentId : null,
                day: cause.end || cause.start,
                label: cause.label,
            });
        },
        [jumpToConflictTarget]
    );

    /**
     * Решение по запросу. Согласование применяется к плану сразу; если план после запроса
     * меняли другие — сервер вернёт расхождения, и пользователь решает, применять ли поверх
     */
    const decideApproval = useCallback(
        async (approval: PlanApproval, approve: boolean, comment?: string) => {
            let result = await store.decideApproval(approval.id, approve, false, comment);

            if (result && !result.applied && result.discrepancies.length) {
                const list = result.discrepancies.map((item) => `• ${item.title} — ${item.modifiedBy}`).join("\n");

                if (!window.confirm(`Пока запрос ждал, план поменяли:\n${list}\n\nСогласовать поверх этих правок?`)) {
                    return;
                }

                result = await store.decideApproval(approval.id, approve, true, comment);
            }

            if (!result || !result.applied) {
                return;
            }

            store.notify.success(
                approve ? "Сдвиг согласован и применён к плану" : "Сдвиг отклонён — автор получит уведомление"
            );

            if (approve && result.sentForApproval && result.sentForApproval.length) {
                store.notify.success(`Дальше по связям — на согласование ГИПам: ${result.sentForApproval.join(", ")}`);
            }

            await loadApprovals();

            if (approve) {
                load();
            }
        },
        [store, load, loadApprovals]
    );

    // переход по ссылке из уведомления: ?approval=<id> — открыть список и показать запрос
    const location = useLocation();
    const approvalFromLink = useMemo(() => new URLSearchParams(location.search).get("approval"), [location.search]);
    const shownApproval = useRef<string | null>(null);

    useEffect(() => {
        if (!approvalFromLink || shownApproval.current === approvalFromLink || !loaded) {
            return;
        }

        const approval = approvals.find((item) => item.id === approvalFromLink);

        if (!approval) {
            return;
        }

        shownApproval.current = approvalFromLink;
        setApprovalsOpen(true);

        if (approval.pushes.length) {
            revealApprovalPush(approval, approval.pushes[0]);
        }
    }, [approvalFromLink, approvals, loaded, revealApprovalPush]);

    /** Конец связи, к которому идём, пока выборка расширяется и график перечитывается */
    const pendingEnd = useRef<PlanLinkEndResponse | null>(null);

    // проект появился на экране — докручиваем к объекту
    useEffect(() => {
        const end = pendingEnd.current;

        if (end && projects.some((project) => project.id === end.orderId)) {
            pendingEnd.current = null;
            revealEnd(end);
        }
    }, [projects, revealEnd]);

    /**
     * Клик по якорю связи, уходящей за выборку (п. 6 ТЗ): перейти к связанному объекту,
     * автоматически расширив выборку, чтобы он попал в кадр. Проект скрыт поиском — поиск
     * снимается. Объект за краем периода — период расширяется до него, не теряя текущих
     * месяцев; не влезает в предел глубины масштаба — переходим к месяцу объекта.
     */
    const goToLinkEnd = useCallback(
        (end: PlanLinkEndResponse) => {
            if (plan.projects.some((item) => item.id === end.orderId)) {
                if (projects.some((item) => item.id === end.orderId)) {
                    revealEnd(end);
                } else {
                    pendingEnd.current = end;
                    store.setQuery("");
                }

                return;
            }

            if (!end.start) {
                return;
            }

            if (store.dirty && !window.confirm("Правки графика не сохранены. Расширить выборку и перечитать график?")) {
                return;
            }

            const target = moment(end.start, "YYYY-MM-DD").startOf("month");
            const from = moment({ year: store.year, month: store.month - 1, day: 1 });
            const to = from.clone().add(store.monthsOnPage - 1, "months");

            const start = moment.min(from, target);
            const stop = moment.max(to, target);
            const months = stop.diff(start, "months") + 1;

            pendingEnd.current = end;
            store.setQuery("");

            if (months <= MONTHS_LIMITS[store.scale].max) {
                store.setMonthsOnPage(months);
                store.setPeriod(start.year(), start.month() + 1);
                store.notify.info(`${end.label}: выборка расширена до ${months} мес., чтобы объект попал в кадр`);
                load(start.year(), start.month() + 1);
            } else {
                store.notify.info(`${end.label}: график переходит к ${target.format("MMMM YYYY")}`);
                store.setPeriod(target.year(), target.month() + 1);
                load(target.year(), target.month() + 1);
            }
        },
        [plan.projects, projects, store, revealEnd, load]
    );

    /**
     * Первый клик по конфликту — раскрыть проект (и раздел) и прокрутить график к началу
     * конфликта, повторный — открыть карточку: сотрудника, а при выходе за срок — проекта
     */
    const selectConflict = useCallback(
        (conflict: PlanConflict) => {
            const orderId = conflict.orderIds[0];

            if (selectedConflict === conflict.id) {
                const to = conflict.kind === "deadline" ? links.order(orderId) : links.employee(conflict.employerId);

                if (to) {
                    navigate(to);
                }

                return;
            }

            setSelectedConflict(conflict.id);
            setFocusedRow(null);

            // раскрыть все проекты конфликта, а в режиме «Разделы» — и разделы его дней
            conflict.orderIds.forEach((id) => store.collapser.set(id, true));

            if (store.detail === "sections") {
                (conflict.cells.length ? conflict.cells : [{ orderId, contentGuid: conflict.contentGuid }]).forEach(
                    (cell) => store.collapser.set(`${cell.orderId}/${cell.contentGuid || "none"}`, true)
                );
            }

            revealRow(conflictRows(conflict)[0], orderId, conflict.from);
        },
        [selectedConflict, links, navigate, store, conflictRows, revealRow]
    );

    const markConflict = useCallback(
        async (conflict: PlanConflict, comment: string) => {
            const saved = await store.markConflict(conflict, comment);

            // пометки идут поверх кэша графика — перечитать дёшево
            if (saved) {
                load();
            }

            return saved;
        },
        [store, load]
    );

    const unmarkConflict = useCallback(
        async (conflict: PlanConflict) => {
            const removed = await store.unmarkConflict(conflict);

            if (removed) {
                load();
            }

            return removed;
        },
        [store, load]
    );

    const projectLabel = useCallback(
        (project: ProjectPlanProject | undefined) => (project ? `${project.fullInventoryNumber} ${project.name}` : ""),
        []
    );

    const milestoneHandlers = useMemo(
        (): MilestoneHandlers => ({
            onHover: setHoveredMilestone,
            // одиночный клик — фокус; повторный по той же вехе его снимает
            onFocus: (active) =>
                setFocusedMilestone((current) => (current && current.key === active.key ? null : active)),
            // двойной клик — окно правки (или просмотра, если прав нет) у самой вехи
            onOpen: (active) => {
                const project = plan.projects.find((item) => item.id === active.projectId);

                setActiveMilestone(null);
                setMilestoneEditor({
                    key: active.key,
                    anchor: active.anchor,
                    items: active.items.map((item) => ({
                        id: item.id,
                        orderId: item.orderId,
                        day: item.day,
                        name: item.name,
                        comment: item.comment,
                    })),
                    column: active.items[0].column,
                    project: projectLabel(project),
                    readOnly: !project || !project.canEditMilestones,
                });
            },
        }),
        [plan.projects, projectLabel, setActiveMilestone]
    );

    /** Новая веха — в том же окне: на день двойного клика или, по кнопке «+», на сегодня */
    const addMilestone = useCallback(
        (project: ProjectPlanProject, column: number | null, anchor: MilestoneAnchor) => {
            const day =
                column !== null && plan.columns[column]
                    ? plan.columns[column].day
                    : moment().format(MILESTONE_DAY_FORMAT);

            setActiveMilestone(null);
            setMilestoneEditor({
                key: `${project.id}:new`,
                anchor,
                items: [{ id: "", orderId: project.id, day, name: "", comment: "" }],
                column: column !== null ? column : -1,
                project: projectLabel(project),
                readOnly: false,
            });
        },
        [plan.columns, projectLabel, setActiveMilestone]
    );

    const saveMilestone = useCallback(
        async (draft: MilestoneDraft) => {
            const saved = await milestones.save(draft);

            if (saved) {
                // график на сервере закэширован, а вехи идут поверх кэша — перечитать дёшево
                load();
            }

            return !!saved;
        },
        [milestones, load]
    );

    const removeMilestone = useCallback(
        async (milestone: Milestone) => {
            const removed = await milestones.remove(milestone);

            if (removed) {
                load();
            }

            return removed;
        },
        [milestones, load]
    );

    /**
     * Выделенная веха подсвечивает свой столбец так же, как клик по дате в шапке: день,
     * а в недельном масштабе — всю неделю, во всю высоту графика и в самой шапке.
     */
    const milestoneColumn = milestoneEditor
        ? milestoneEditor.column
        : activeMilestone
          ? activeMilestone.items[0].column
          : -1;
    const milestoneSlot = milestoneColumn >= 0 && milestoneColumn < columnsCount ? grid.slotOf[milestoneColumn] : null;

    const milestoneRange = useMemo(
        () => (milestoneSlot !== null ? slotRange(grid, milestoneColumn) : null),
        [grid, milestoneSlot, milestoneColumn]
    );

    return (
        <div className={styles.page}>
            <LocalLoader active={store.isLoading || !loaded} />

            <div className={styles.toolbar}>
                <div className={styles.view}>
                    <div className={styles.selects}>
                        <div className={styles.scale} title="Масштаб сетки: дни или недели">
                            <SimpleSelect
                                className={`full-width-select ${Classes.FILL}`}
                                filterable={false}
                                activeItem={scaleOption}
                                items={SCALE_OPTIONS}
                                itemRenderer={renderSingleOption}
                                onItemSelect={(item: SelectItem) => changeScale(item.id as PlanScale)}
                            >
                                <DefaultSelectedOption option={scaleOption} />
                            </SimpleSelect>
                        </div>

                        <div className={styles.months} title="Сколько месяцев показывать в этом масштабе">
                            <SimpleSelect
                                className={`full-width-select ${Classes.FILL}`}
                                filterable={false}
                                activeItem={monthsOption}
                                items={monthsOptions}
                                itemRenderer={renderSingleOption}
                                onItemSelect={(item: SelectItem) => changeMonthsOnPage(+item.id)}
                            >
                                <DefaultSelectedOption option={monthsOption} />
                            </SimpleSelect>
                        </div>

                        <div className={styles.detail} title="Во что раскрывается проект">
                            <SimpleSelect
                                className={`full-width-select ${Classes.FILL}`}
                                filterable={false}
                                activeItem={detailOption}
                                items={DETAIL_OPTIONS}
                                itemRenderer={renderSingleOption}
                                onItemSelect={(item: SelectItem) => store.setDetail(item.id as ProjectPlanDetail)}
                            >
                                <DefaultSelectedOption option={detailOption} />
                            </SimpleSelect>
                        </div>

                        <div className={styles.sort} title="Порядок проектов">
                            <SimpleSelect
                                className={`full-width-select ${Classes.FILL}`}
                                filterable={false}
                                activeItem={sortOption}
                                items={SORT_OPTIONS}
                                itemRenderer={renderSingleOption}
                                onItemSelect={(item: SelectItem) => store.setSort(item.id as ProjectPlanSort)}
                            >
                                <DefaultSelectedOption option={sortOption} />
                            </SimpleSelect>
                        </div>
                    </div>

                    <div className={styles.hintRow}>
                        {/* во второй строке, а не среди селектов: там он выталкивал поиск за край */}
                        <div
                            className={styles.busyToggle}
                            data-tip="Занятость сотрудника на других проектах и его отсутствия"
                            data-tip-note="В строке сотрудника: серым — другие проекты, цветом — отпуск, больничный, БС. Видно, чем он занят и куда можно сдвинуть работу. Конфликты считаются и при выключенном"
                        >
                            <Switch
                                checked={store.showBusy}
                                label="Другие проекты"
                                onChange={() => store.setShowBusy(!store.showBusy)}
                            />
                        </div>

                        <div
                            className={styles.busyToggle}
                            data-tip="Стрелки связей между работами и разделами"
                            data-tip-note="На большом проекте их бывает много — тумблер прячет. Нарушенная связь — красная"
                        >
                            <Switch
                                checked={store.showLinks}
                                label="Связи"
                                onChange={() => store.setShowLinks(!store.showLinks)}
                            />
                        </div>

                        <PlanrButton
                            type="lightblueish"
                            size="small"
                            style={HELP_BUTTON_STYLE}
                            onClick={() => setHelpOpen(true)}
                        >
                            Инструкция по работе
                        </PlanrButton>
                    </div>
                </div>

                <div className={styles.period}>
                    <PeriodRangeSlider
                        year={store.year}
                        month={store.month}
                        monthsCount={store.monthsOnPage}
                        onChange={gotoMonth}
                    />

                    <div className={styles.norms}>
                        <span className={styles.norm}>
                            Проектов: <b>{projects.length}</b>
                        </span>

                        {conflicts.length > 0 && (
                            <span
                                className={unresolved.length ? styles.conflicts : styles.conflictsResolved}
                                onClick={() => setConflictsOpen(!conflictsOpen)}
                                data-tip={
                                    unresolved.length
                                        ? "Перегрузка, работа на отсутствии или в нерабочий день, выход за срок"
                                        : "Все конфликты помечены допустимыми"
                                }
                            >
                                Конфликты: <b>{unresolved.length}</b>
                            </span>
                        )}

                        <ApprovalsPanel
                            approvals={approvals}
                            busy={store.draftBusy}
                            isOpen={approvalsOpen}
                            onOpenChange={setApprovalsOpen}
                            onReveal={revealApprovalPush}
                            onCause={revealApprovalCause}
                            onDecide={decideApproval}
                        />
                    </div>
                </div>

                <div className={styles.actions}>
                    <Search query={store.query} onSearch={store.setQuery} />
                    <Zoomer zoom={store.zoom} min={50} max={100} step={5} onChange={store.setZoom} />
                    <CollapseExpandAll onChange={store.collapser.setAll} />
                </div>
            </div>

            <div className={styles.scroll} onScroll={onGridScroll} ref={scrollRef}>
                {/*
                    Зум сетки — через CSS zoom, а не transform: scale. Transform заводит
                    свой контекст координат: липкая шапка дней внутри него перестаёт липнуть
                    на любом масштабе, кроме 100%, и уезжает вверх вместе со строками.
                    zoom масштабирует саму раскладку, поэтому sticky работает как обычно,
                    а ширина прокрутки совпадает с видимой сеткой (при scale контейнер
                    оставался прежней ширины, и справа висела пустота). Жесты не страдают:
                    все замеры идут через getBoundingClientRect (trackScale / columnAt).
                */}
                <ConflictJumpContext.Provider value={jumpToConflictTarget}>
                    <div className={styles.grid} ref={gridRef} style={{ zoom: store.transform }}>
                        {highlightKeys && (
                            <BlockHighlight gridRef={gridRef} zoom={store.transform} keys={highlightKeys} />
                        )}

                        <PlanDayHeader
                            periods={periods}
                            grid={grid}
                            selectedSlot={milestoneSlot !== null ? milestoneSlot : selectedSlot}
                            onSelectSlot={onSelectSlot}
                        />

                        {projects.map((project) => (
                            <ProjectSection
                                key={project.id}
                                project={project}
                                grid={grid}
                                monthBreaks={monthBreaks}
                                selectedSlot={milestoneRange || selectedRange}
                                collapsed={!store.collapser.opened.get(project.id)}
                                onToggle={store.collapser.toggle}
                                detail={store.detail}
                                links={links}
                                collapser={store.collapser}
                                columnsCount={columnsCount}
                                activeMilestoneKey={
                                    milestoneEditor ? milestoneEditor.key : activeMilestone ? activeMilestone.key : null
                                }
                                milestoneHandlers={milestoneHandlers}
                                onAddMilestone={addMilestone}
                                viewport={viewport}
                                onReveal={revealOnTrack}
                                editMode={canEdit}
                                editableEmployers={editableEmployers}
                                days={days}
                                onOperation={onOperation}
                                onShiftFrom={setShiftFrom}
                                conflicts={conflictsByProject.get(project.id)}
                                columns={columnOf}
                                highlightedRows={highlightedRows}
                                absences={absences}
                                busy={busy}
                                onBusyClick={goToBusy}
                                onOpenWorkLinks={openWorkLinks}
                                onOpenSectionLinks={openSectionLinks}
                                linkConnectable={canEdit && store.showLinks}
                            />
                        ))}

                        {/* Слой — только при включённых «Связях»: выключены — ни стрелок, ни точек для
                        протягивания. Нужен и без связей: в нём точки, от которых тянут новую */}
                        {store.showLinks && (planLinks.length > 0 || canEdit || !!linkDrag) && (
                            // правый клик по стрелке → «Удалить связь» → подтверждение
                            <RemoveConfirmation<PlanLinkResponse>
                                what={(link) =>
                                    `связь ${linkCaption(link.type, link.lag)}: ${link.from ? link.from.label : ""} → ${
                                        link.to ? link.to.label : ""
                                    }`
                                }
                                onConfirmed={(link) => removeLink(link.id)}
                            >
                                {({ confirmRemoving }) => (
                                    <LinksLayer
                                        links={planLinks}
                                        onStartLink={canEdit ? startLink : undefined}
                                        preview={
                                            linkDrag
                                                ? {
                                                      from: { x: linkDrag.start.x, y: linkDrag.start.y },
                                                      to: { x: linkDrag.x, y: linkDrag.y },
                                                  }
                                                : null
                                        }
                                        gridRef={gridRef}
                                        scrollRef={scrollRef}
                                        trackOffset={PROJECT_TRACK_OFFSET}
                                        zoom={store.transform}
                                        violated={violatedLinks}
                                        orders={visibleOrders}
                                        selectedId={selectedLink}
                                        onSelect={setSelectedLink}
                                        onRemove={(link) => removeLink(link.id)}
                                        onConfirmRemove={confirmRemoving}
                                        onGoTo={goToLinkEnd}
                                        placeOf={placeOfEnd}
                                        columnX={columnX}
                                    />
                                )}
                            </RemoveConfirmation>
                        )}
                    </div>
                </ConflictJumpContext.Provider>
            </div>

            {linksTarget && (
                <LinksDialog
                    target={linksTarget}
                    links={planLinks}
                    onSave={saveLink}
                    onRemove={removeLink}
                    onSearch={searchLinkTargets}
                    onClose={() => setLinksTarget(null)}
                />
            )}

            {/* подсказка от наведения; пока открыто окно правки — не мешает */}
            {activeMilestone && !milestoneEditor && <MilestonePlate active={activeMilestone} />}

            {milestoneEditor && (
                <MilestoneEditor
                    key={milestoneEditor.key}
                    anchor={milestoneEditor.anchor}
                    items={milestoneEditor.items}
                    project={milestoneEditor.project}
                    readOnly={milestoneEditor.readOnly}
                    onSave={saveMilestone}
                    onRemove={removeMilestone}
                    onClose={closeMilestoneEditor}
                />
            )}

            {loaded && !store.isLoading && !projects.length && <NoData className={styles.nodata} />}

            {conflictsOpen && (
                <ConflictsPanel
                    conflicts={conflicts}
                    projects={plan.projects}
                    selectedId={selectedConflict}
                    canMark={canMarkConflict}
                    onSelect={selectConflict}
                    onMark={markConflict}
                    onUnmark={unmarkConflict}
                    onClose={() => {
                        setConflictsOpen(false);
                        setSelectedConflict(null);
                    }}
                />
            )}

            {shiftFrom && (
                <ShiftFromDialog target={shiftFrom} onClose={() => setShiftFrom(null)} onSubmit={submitShiftFrom} />
            )}

            <ProjectPlanHelpDialog isOpen={helpOpen} canEdit={canEdit} onClose={() => setHelpOpen(false)} />

            {/* полоса кнопок — как в Табеле План: есть правки или сохранённый черновик */}
            {(operations.length > 0 || store.hasDraft) && (
                <div className={styles.submitBar}>
                    <PlanDraftPanel
                        busy={store.draftBusy}
                        dirty={store.dirty}
                        hasDraft={store.hasDraft}
                        canUndo={store.history.length > 0}
                        projects={plan.projects}
                        conflicts={unresolved}
                        preview={localDraft.view}
                        onRevealPush={revealPush}
                        onSave={store.saveDraft}
                        onCancel={store.cancelOperations}
                        onUndo={store.undoOperation}
                        onDiscard={store.discardDraft}
                        onReview={store.fetchDraftView}
                        onApply={applyDraftChanges}
                    />
                </div>
            )}
        </div>
    );
});

/** Конец связи по ключу полосы: work:<id> или section:<order>/<guid> */
function parseEnd(key: string): PlanLinkEndInput {
    const [kind, rest] = [key.slice(0, key.indexOf(":")), key.slice(key.indexOf(":") + 1)];

    if (kind === "section") {
        const [orderId, contentGuid] = rest.split("/");
        return { kind: "section", orderId, contentGuid };
    }

    return { kind: "work", assignmentId: rest };
}
